import type {BaseSchedule} from '@domain/schedule'
import type {AssetSnapshot} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {Skeleton, Fade, Stack} from '@mui/material'
import {Moment} from 'moment-timezone'
import React, {memo} from 'react'

import {usePlanningComments} from '../hooks'

import {CommentSidebarHeader} from './CommentSidebarHeader'
import {CommentSidebarList} from './CommentSidebarList'

interface CommentsSidebarProps {
  width: number
  isOpen: boolean
  closeSidebar: () => void
  isReadOnly: boolean
  schedule: BaseSchedule
  assets: AssetSnapshot[]
  startOfPlan: Moment
  endOfPlan: Moment
  startOfChart: Moment
  endOfChart: Moment
  onSelectScheduleItem?: (id: string) => void
}

const _CommentsSidebar: React.FC<CommentsSidebarProps> = ({
  width,
  isOpen,
  closeSidebar,
  isReadOnly,
  schedule,
  assets,
  startOfPlan,
  endOfPlan,
  startOfChart,
  endOfChart,
  onSelectScheduleItem
}) => {
  const {comments} = usePlanningComments({
    schedule,
    assets,
    scheduleItemRange: [startOfChart, endOfChart]
  })

  return (
    <Fade in={isOpen}>
      <CardBox
        sx={{
          width,
          position: 'absolute',
          right: 0,
          // negative top to compensate for the card content padding
          top: -24,
          bottom: 0,
          zIndex: 3,
          overflowY: 'auto',
          pt: 0,
          px: 0,
          display: 'flex',
          flexDirection: 'column'
        }}
        {...dataTestId('comments_sidebar')}
      >
        <CommentSidebarHeader comments={comments} onCloseSidebar={closeSidebar} />
        <Stack sx={{p: 3, flex: 1}} spacing={3}>
          {comments ? (
            <CommentSidebarList
              comments={comments}
              isReadOnly={isReadOnly}
              schedule={schedule}
              startOfPlan={startOfPlan}
              endOfPlan={endOfPlan}
              onSelectScheduleItem={onSelectScheduleItem}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              height={'100%'}
              {...dataTestId('comments_sidebar_skeleton')}
            />
          )}
        </Stack>
      </CardBox>
    </Fade>
  )
}

export const CommentsSidebar = memo(_CommentsSidebar)
